import { accessControl } from 'lib-shared-access-control';
import {
  AuthInitError,
  authManager,
  noTokenRedirect,
  parseAccessToken,
  PENDING_REASON_OKTA_HANDLE_REDIRECT,
  PENDING_REASON_OKTA_SIGNIN_REDIRECT,
} from 'lib-shared-auth';
import { electron } from 'lib-shared-electron';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import type { Organization } from 'lib-shared-types-api';
import { User } from 'lib-shared-types-api';
import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';

import { loginApi } from 'apis/login';
import { appActions } from 'app/app.actions';
import { AppError } from 'app/types/app.types';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { presenceActions } from 'components/presence/actions/presenceActions';
import { actions as telephonyActions } from 'components/telephony/actions/telephony';
import { electronActions } from 'middleware/electron/electron.actions';
import { websocketActions } from 'middleware/websocket/actions/websocket';
import { debugLog } from 'utils/debugLog';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import {
  requestMicrophoneAccess,
  RequestMicrophoneAccessError,
} from 'utils/telephony/microphone';

import { testBrowserFeatures } from './utils/browserFeatures/browserFeatures';
import { initPartnerIntegrationsEvents } from './utils/partners/partners';
import {
  authResolverErrorFallThroughValue,
  useAuthDataResolver,
} from './utils/useAuthDataResolver';
import { useDesktopAppNotice } from './utils/useDesktopAppNotice/useDesktopAppNotice';
import { isDesktopAppVersionBelow } from './utils/useDesktopAppNotice/utils/isDesktopAppVersionBelow';
import { useDispatchLegacyActions } from './utils/useDispatchLegacyActions/useDispatchLegacyActions';
import { usePubSubGlobalEvents } from './utils/usePubSubGlobalEvents/usePubSubGlobalEvents';
import { useScheduleConflictsNotice } from './utils/useScheduleConflictsNotice/useScheduleConflictsNotice';
import { useThirdPartyScripts } from './utils/useThirdPartyScripts/useThirdPartyScripts';

/** Represents an error from the app init check. */
class AppCheckError extends Error {
  constructor(readonly appError: AppError) {
    super();
  }
}

/** A hook that initializes the application. */
export const useAppInit = (): void => {
  const dispatch = useAppDispatch();
  const resolveAuthData = useAuthDataResolver();
  const dispatchLegacyActions = useDispatchLegacyActions();
  const skipMicrophoneAccess = useAppSelector(
    (state) => state.settings.personal.skipMicrophoneAccess,
  );
  const desktopAppNoticeModal = useDesktopAppNotice();
  const notifyOnScheduleConflicts = useScheduleConflictsNotice();
  const initThirdPartyScripts = useThirdPartyScripts();

  usePubSubGlobalEvents();

  /** Checks whether there is an active internet connection. */
  const checkNetwork = useCallback(() => {
    if (electron.isElectron() && !navigator.onLine) {
      throw new AppCheckError({ type: 'noInternetConnection' });
    }
  }, []);

  /** Checks whether the browser has the required capabilities. */
  const checkBrowserFeatures = useCallback(() => {
    const { failed: failedBrowserFeatures } = testBrowserFeatures();

    if (failedBrowserFeatures.length) {
      throw new AppCheckError({
        type: 'unsupportedBrowserFeatures',
        arg: {
          failedBrowserFeatures,
        },
      });
    }
  }, []);

  /**
   * Checks whether the organization isn't suspended.
   *
   * @param organization The organization.
   */
  const checkOrganization = useCallback<(organization: Organization) => void>(
    async (organization) => {
      if (organization.suspended) {
        const authData = await authManager.getAuthData();
        await dispatch(
          appActions.logOut({
            refreshToken: authData?.refresh_token,
          }),
        );
        Sentry.captureMessage('Organization suspended', {
          level: 'debug',
          tags: {
            team: SentryTeam.Platform,
          },
        });
        console.error('Organization suspended');
        authManager.removeAuthData();
        throw new AppCheckError({ type: 'organizationSuspended' });
      }
    },
    [dispatch],
  );

  /**
   * Checks whether the agent has the sufficient roles.
   *
   * @param user The user.
   */
  const checkAgent = useCallback((user: User) => {
    if (user.type !== 'member') {
      throw new AppCheckError({ type: 'agentInsufficientRoles' });
    }
  }, []);

  /** Initializes the auth. */
  const initAuth = useCallback(async () => {
    try {
      const authDataResult = await resolveAuthData();

      if (authDataResult.asyncStatus === 'fulfilled') {
        await authManager.init(authDataResult.authData.refresh_token);
        dispatch(appActions.logIn({ type: authDataResult.type }));
      }

      return authDataResult;
    } catch (error) {
      if (error === authResolverErrorFallThroughValue) {
        // When resolveAuthData rejects normally with message 'fallThrough'.
        debugLog('AUTH', 'Unable to resolve any of available auth methods');
      } else {
        if (error) {
          debugLog('AUTH', error.toString());
        }

        if (error instanceof AuthInitError && error.cause === 'expired') {
          // We can assume the token was expired and that no real error occurred.
          ampli.authFailedInit({ failureReason: 'refreshTokenExpired' });
        } else {
          ampli.authFailedInit({ failureReason: 'unknown' });

          Sentry.captureException(error, {
            level: 'error',
            tags: {
              team: SentryTeam.Platform,
              errorSource: 'useAppInit',
            },
          });
        }
      }

      noTokenRedirect();
      return Promise.reject(error);
    }
  }, [dispatch, resolveAuthData]);

  /**
   * Initializes the feature flags.
   *
   * This conditionally sets up the feature flags with or without the
   * user/organization being passed in from the WebSocket. When omitted, the
   * feature flags are initialized with the parsed auth data token data, which
   * contains the user and organization - otherwise we initialize with the
   * provided user and organization.
   *
   * @param user The user.
   * @param organization The organization.
   */
  const initFeatureFlags = useCallback(
    async (user?: User, organization?: Organization) => {
      if (!user || !organization) {
        const authData = await authManager.getAuthData();
        if (!authData) return;
        const accessTokenPayload = parseAccessToken(authData.access_token);
        const {
          iss: organizationId,
          user_id: userId,
          sub: userEmail,
        } = accessTokenPayload;

        // On initial load we won't yet have a user/organization.
        // We therefore try to init featureFlag using a best-guess
        // based on the hostname/subdomain. Then exit early.
        const [first, second] = location.hostname.split('.');
        const subdomain = second ? first : 'demo';
        await featureFlags.init(
          { id: userId, email: userEmail, type: 'member' },
          {
            id: organizationId,
            name: subdomain,
            subdomain,
          },
        );
        return;
      }

      // We dispatch the pending/fulfilled flags only when initializing with
      // the user and organization from the WebSocket so that once the app
      // loads, flags are properly evaluated on LaunchDarkly.
      dispatch(appActions.getFeatureFlags.pending());
      const { flags } = await featureFlags.init(user, organization);
      dispatch(appActions.getFeatureFlags.fulfilled({ flags }));
    },
    [dispatch],
  );

  /** Initializes the WebSocket. */
  const initWebSocket = useCallback(async () => {
    // TODO: [TEAMS-7175] Refactor this when WebSocket is migrated.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wsCreateResponse: any = await bindRtkActionToPromise(
      dispatch,
      websocketActions.create,
    )({ init: true });

    if (ampli.isLoaded) {
      ampli.websocketConnectionCreated({ source: 'init' });
    }

    return wsCreateResponse.params;
  }, [dispatch]);

  /** Initializes the microphone. */
  const initMicrophone = useCallback(() => {
    /**
     * Handles the scenario when there is no microphone. This in turn disables
     * the phone feature and sets the voice based channels to not working.
     */
    const handleNoMicrophone = () => {
      dispatch(telephonyActions.telephonySetBlocked());
      dispatch(
        presenceActions.presenceSetMedia({
          media: 'speak',
          working: false,
        }),
      );
    };

    if (
      skipMicrophoneAccess ||
      !featureFlags.get(['phone-channel']).phoneChannel
    ) {
      handleNoMicrophone();
    } else {
      requestMicrophoneAccess().catch((error: RequestMicrophoneAccessError) => {
        handleNoMicrophone();
        dispatch(
          appActions.showModal({
            modal: {
              type: 'app:microphoneError',
              arg: { error: error.name },
            },
          }),
        );
      });
    }
  }, [dispatch, skipMicrophoneAccess]);

  /** Checks feature flag and initializes desktop app notice modal */
  const initDesktopAppNotice = useCallback(() => {
    if (featureFlags.get(['desktop-app-notice-modal']).desktopAppNoticeModal) {
      desktopAppNoticeModal();
    }
  }, [desktopAppNoticeModal]);

  /**
   * Checks feature flag and initializes desktop app pwa notice modal for 4.0.6
   * and above desktop app
   */
  const initDesktopAppPwaNotice = useCallback(async () => {
    if (!electron.isElectron()) {
      return;
    }

    const currentVersionIsBelow = await isDesktopAppVersionBelow('4.0.6');
    if (!currentVersionIsBelow) {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:desktopAppPwaNotice',
          },
        }),
      );
    }
  }, [dispatch]);

  /** Initializes the user data. */
  const initUser = useCallback(async (user: User) => {
    try {
      const authData = await authManager.getAuthData();
      if (!authData) return;

      await accessControl.init({ user });
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  /** Initializes the data needed to switch organizations */
  const initOrganizations = useCallback(
    async (organization: Organization) => {
      try {
        const { data: organizationsData } = await dispatch(
          loginApi.endpoints.readAuthOrganizations.initiate(),
        );

        dispatch(
          appActions.setOrganization({
            organization: organization,
          }),
        );

        if (organizationsData) {
          dispatch(
            appActions.setOrganizations({
              organizations: organizationsData.data.organizations,
            }),
          );
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [dispatch],
  );

  /** Fetches and caches organization- and user server storage data. */
  const initServerStorage = useCallback(async () => {
    try {
      await dispatch(appActions.getServerStorage());
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [dispatch]);

  const initManifest = useCallback(() => {
    if (!document.querySelector('[rel="manifest"]')) {
      const isMobilePlatform =
        navigator.userAgent.match(/(Mobile)|(Android)/gi);
      const linkElement = document.createElement('link');

      linkElement.rel = 'manifest';
      linkElement.href = isMobilePlatform
        ? `${BASEPATH}pwa/mobile-manifest.json`
        : `${BASEPATH}pwa/manifest.json`;
      document.head.appendChild(linkElement);
    }
  }, []);

  const initPwaLaunchQueue = useCallback(() => {
    window.launchQueue?.setConsumer((launchParams) => {
      if (!launchParams.targetURL) {
        return;
      }

      const params = new URL(launchParams.targetURL).searchParams;
      const url = params.get('url');
      if (url?.startsWith('mailto')) {
        dispatch(electronActions.composeEmail({ mailto: url }));
      } else if (url?.startsWith('tel')) {
        dispatch(
          electronActions.makeCall({ phoneNumber: url.replace('tel:', '') }),
        );
      }
    });
  }, [dispatch]);

  /** Triggers the initialization flow. */
  const init = useCallback(async () => {
    try {
      checkNetwork();
      checkBrowserFeatures();

      const result = await initAuth();
      await initFeatureFlags();

      if (result?.asyncStatus === 'pending') {
        // Okta redirects occur asynchronously
        // At this point auth is not yet ready
        // And if allowed to continue the app will enter into a race condition
        // Where either Okta redirects or the app eventually does a NoTokenRedirect to the accounts page.
        // To prevent this we stop the initialization flow here, and let Okta handle the redirect. :fingers-crossed:

        if (result?.pendingReason === PENDING_REASON_OKTA_HANDLE_REDIRECT) {
          throw new AppCheckError({
            type: 'oktaHandleRedirect',
          });
        }
        if (result?.pendingReason === PENDING_REASON_OKTA_SIGNIN_REDIRECT) {
          throw new AppCheckError({
            type: 'oktaHandleSigninRedirect',
          });
        }

        Sentry.captureMessage('Auth: Result Pending', {
          level: 'debug',
          tags: {
            team: SentryTeam.Platform,
          },
          extra: {
            reason: result?.pendingReason,
          },
        });
      }

      const authData = await authManager.getAuthData();
      if (!authData) {
        console.warn('No auth data found');
        console.warn('Continuing without auth data 💣💥');
      }

      const { user, organization, connection_id } = await initWebSocket();
      await initUser(user);
      await initFeatureFlags(user, organization);

      checkAgent(user);
      checkOrganization(organization);

      const flags = featureFlags.get(['organizations-sidebar']);

      initManifest();
      initPwaLaunchQueue();

      if (flags.organizationsSidebar) {
        await initOrganizations(organization);
      }

      initPartnerIntegrationsEvents();

      initServerStorage();
      initThirdPartyScripts({
        user,
        organization,
        connectionId: connection_id,
      });
      initMicrophone();
      initDesktopAppNotice();
      initDesktopAppPwaNotice();

      notifyOnScheduleConflicts();

      dispatchLegacyActions(user, organization);

      document.title = organization.name;
    } catch (error) {
      if (error instanceof AppCheckError) {
        dispatch(appActions.setError({ error: error.appError }));
      }
    }
  }, [
    checkAgent,
    checkBrowserFeatures,
    checkNetwork,
    checkOrganization,
    dispatch,
    dispatchLegacyActions,
    initAuth,
    initDesktopAppNotice,
    initDesktopAppPwaNotice,
    initFeatureFlags,
    initManifest,
    initMicrophone,
    initOrganizations,
    initPwaLaunchQueue,
    initServerStorage,
    initThirdPartyScripts,
    initUser,
    initWebSocket,
    notifyOnScheduleConflicts,
  ]);

  // Kicks off the app initialization.
  useEffectOnce(() => {
    init();
  });
};
